import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  BarcodeCapture,
  barcodeCaptureLoader,
  BarcodeCaptureOverlay,
  BarcodeCaptureSettings,
  Symbology,
} from "@scandit/web-datacapture-barcode";
import {
  Camera,
  CameraSwitchControl,
  DataCaptureContext,
  DataCaptureView,
  FrameSourceState,
  LaserlineViewfinder,
} from "@scandit/web-datacapture-core";
import { TorchSwitchControl } from "@scandit/web-datacapture-core";
import logger from 'src/utils/logger';

const SDKStatus = {
  Uninitialized: "uninitialized",
  Initializing: "initializing",
  Ready: "ready",
  Error: "error",
};

export const SDKContext = createContext(null);

class SDKController {
  constructor() {
    this.state = {
      context: null,
      status: SDKStatus.Uninitialized,
      isScanning: false,
      isCameraOn: false,
      cameraPermissionDenied: false, // Kamera izni reddedildiyse true
    };
  }

  async initialize() {
    if (this.state.status !== SDKStatus.Uninitialized) return;

    try {
      this.state.status = SDKStatus.Initializing;

      this.state.context = await DataCaptureContext.forLicenseKey(process.env.REACT_APP_SCANDIT_KEY, {
        // libraryLocation: "https://cdn.jsdelivr.net/npm/@scandit/web-datacapture-barcode@8.1.0/library/engine/",
        libraryLocation: new URL("/library/engine/", document.baseURI).toString(),
        moduleLoaders: [barcodeCaptureLoader()],
      });

      await this.initializeComponents();
      this.state.status = SDKStatus.Ready;
    } catch (error) {
      this.state.status = SDKStatus.Error;
      logger.error("Scandit SDK initialization failed:", error);
      this.state.error = error;
      throw error;
    }
  }


  async initializeComponents() {
    this.state.cameraSwitchControl = new CameraSwitchControl();

    // 8.x serisinde genellikle bu isimle bulunur:
    try {
      this.state.torchControl = new TorchSwitchControl();
    } catch (e) {
      // Torch control desteklenmiyorsa sessizce devam et
    }

    this.state.view = new DataCaptureView();
    this.state.host = this.createHostElement();
    this.state.view.connectToElement(this.state.host);

    // Kontrolleri ekle
    if (this.state.cameraSwitchControl) this.state.view.addControl(this.state.cameraSwitchControl);
    if (this.state.torchControl) this.state.view.addControl(this.state.torchControl);

    this.state.settings = new BarcodeCaptureSettings();
    this.state.settings.enableSymbologies([
      Symbology.Code128,
      Symbology.Code39,
      Symbology.QR,
      Symbology.DataMatrix,
    ]);

    try {
      this.state.camera = Camera.pickBestGuess();
      if (this.state.camera) {
        await this.state.camera.applySettings(BarcodeCapture.recommendedCameraSettings);
        await this.state.context.setFrameSource(this.state.camera);
      }
    } catch (cameraError) {
      // NotAllowedError, NotReadableError gibi kamera izin hatalarını handle et
      if (cameraError.name === 'NotAllowedError' || 
          cameraError.name === 'NotReadableError' || 
          cameraError.name === 'NotFoundError' ||
          cameraError.name === 'OverconstrainedError' ||
          cameraError.message?.includes('Permission denied') ||
          cameraError.message?.includes('Permission dismissed')) {
        logger.warn("Camera permission issue:", cameraError.name);
        this.state.cameraPermissionDenied = true; // İzin reddedildi olarak işaretle
        // Kullanıcıya bildirim göster
        if (typeof window !== 'undefined' && window.showPermissionWarning) {
          window.showPermissionWarning('camera');
        }
      } else {
        logger.error("Camera initialization error:", cameraError);
      }
      // Kamera hatası olsa bile diğer bileşenler kurulmaya devam etsin
      this.state.camera = null;
    }

    try {
      this.state.barcodeCapture = await BarcodeCapture.forContext(this.state.context, this.state.settings);
      await this.state.view.setContext(this.state.context);

      this.state.overlay = await BarcodeCaptureOverlay.withBarcodeCaptureForView(
        this.state.barcodeCapture,
        this.state.view
      );

      this.state.laserlineViewfinder = new LaserlineViewfinder();
    } catch (barcodeCaptureError) {
      logger.error("Barcode capture initialization error:", barcodeCaptureError);
    }
  }

  async toggleTorch() {
    if (this.state.camera) {
      const currentState = this.state.camera.desiredTorchState;
      this.state.camera.switchToDesiredState(
        currentState === "on" ? FrameSourceState.Off : FrameSourceState.On
      );
      // Veya doğrudan:
      this.state.camera.desiredTorchState = currentState === "on" ? "off" : "on";
    }
  }


  createHostElement() {
    const element = document.createElement("div");
    element.style.display = "none";
    element.style.width = "100%";
    element.style.height = "100%";
    document.body.append(element);
    return element;
  }

  async enableScanning(enabled) {
    if (this.state.status !== SDKStatus.Ready) return;
    
    // Kamera izni daha önce reddedildiyse, tekrar deneme
    if (enabled && this.state.cameraPermissionDenied) {
      logger.warn("Camera permission was previously denied, skipping scan attempt");
      if (typeof window !== 'undefined' && window.showPermissionWarning) {
        window.showPermissionWarning('camera');
      }
      return;
    }

    try {
      if (enabled) {
        if (!this.state.isScanning) {
          // Kamera açılmadan önce host elementini gizli tutup, 
          // kamera hazır olduğunda göstermek eski kareyi görmeyi engeller.
          if (this.state.host) this.state.host.style.opacity = "0";

          try {
            await this.enableCamera(true);
          } catch (cameraErr) {
            // Kamera izin hatalarını handle et ve kullanıcıya bildir
            if (cameraErr.name === 'NotAllowedError' || 
                cameraErr.name === 'NotReadableError' ||
                cameraErr.message?.includes('Permission denied') ||
                cameraErr.message?.includes('Permission dismissed')) {
              logger.warn("Camera permission denied during scanning:", cameraErr.name);
              this.state.cameraPermissionDenied = true; // İzin reddedildi olarak işaretle
              // Kullanıcıya bildirim göster
              if (typeof window !== 'undefined' && window.showPermissionWarning) {
                window.showPermissionWarning('camera');
              }
              return; // İzin yoksa taramayı başlatma
            }
            throw cameraErr;
          }
          if (this.state.barcodeCapture) await this.state.barcodeCapture.setEnabled(true);

          // Küçük bir gecikme veya kamera state kontrolü ile görünür yapın
          setTimeout(() => {
            if (this.state.host) this.state.host.style.opacity = "1";
          }, 100);

          this.state.isScanning = true;
        }
      } else {
        if (this.state.barcodeCapture) await this.state.barcodeCapture.setEnabled(false);
        await this.enableCamera(false);

        // Tarama bittiğinde elementi gizle ki bir sonraki açılışta eski kare gözükmesin
        if (this.state.host) this.state.host.style.opacity = "0";
        this.state.isScanning = false;
      }
    } catch (error) {
      logger.error("Scanning enable/disable error:", error);
      // Hatayı yutuyoruz, global promise rejection olmaması için
    }
  }

  async enableCamera(enabled) {
    if (this.state.status !== SDKStatus.Ready || this.state.isCameraOn === enabled) return;
    
    // Kamera izni daha önce reddedildiyse ve açmaya çalışıyorsak, tekrar deneme
    if (enabled && this.state.cameraPermissionDenied) {
      return;
    }
    
    try {
      if (this.state.context && this.state.context.frameSource) {
        await this.state.context.frameSource.switchToDesiredState(enabled ? FrameSourceState.On : FrameSourceState.Off);
        this.state.isCameraOn = enabled;
      }
    } catch (error) {
      // NotAllowedError gibi izin hatalarını handle et ve kullanıcıya bildir
      if (error.name === 'NotAllowedError' || 
          error.name === 'NotReadableError' ||
          error.name === 'NotFoundError' ||
          error.message?.includes('Permission denied') ||
          error.message?.includes('Permission dismissed')) {
        logger.warn("Camera permission issue in enableCamera:", error.name);
        this.state.cameraPermissionDenied = true; // İzin reddedildi olarak işaretle
        // Kullanıcıya bildirim göster
        if (typeof window !== 'undefined' && window.showPermissionWarning) {
          window.showPermissionWarning('camera');
        }
        this.state.isCameraOn = false;
        return; // Hata fırlatmadan dön
      }
      logger.error("Camera enable/disable error:", error);
      // Hatayı yutuyoruz, global promise rejection olmaması için
    }
  }

  connectToElement(element) {
    if (!this.state.host || this.state.status !== SDKStatus.Ready) return;
    this.state.host.style.display = "block";
    element.append(this.state.host);
  }

  detachFromElement() {
    if (!this.state.host) return;
    this.state.host.style.display = "none";
    // Alternatif olarak içindeki canvas'ı temizlemeyi deneyebilirsiniz ama 
    // Scandit bunu otomatik yönettiği için riskli olabilir. 
    // En iyisi opacity veya display yönetimini enableScanning içinde yapmaktır.
    document.body.append(this.state.host);
  }

  addBarcodeCaptureListener(listener) {
    if (this.state.barcodeCapture) this.state.barcodeCapture.addListener(listener);
  }

  removeBarcodeCaptureListener(listener) {
    if (this.state.barcodeCapture) this.state.barcodeCapture.removeListener(listener);
  }

  async cleanup() {
    try {
      if (this.state.barcodeCapture) await this.state.barcodeCapture.setEnabled(false);
    } catch (e) {
      logger.error("Barcode capture disable error:", e);
    }
    try {
      if (this.state.context && this.state.context.frameSource) {
        await this.state.context.frameSource.switchToDesiredState(FrameSourceState.Off);
      }
    } catch (e) {
      logger.error("Frame source disable error:", e);
    }
    try {
      if (this.state.context) await this.state.context.dispose();
    } catch (e) {
      logger.error("Context dispose error:", e);
    }
    if (this.state.host) this.state.host.remove();
    this.state.status = SDKStatus.Uninitialized;
  }
}

export const SDKProvider = ({ children }) => {
  const [status, setStatus] = useState(SDKStatus.Uninitialized);
  const [error, setError] = useState(null);
  const sdkReference = useRef(new SDKController());

  useEffect(() => {
    const sdk = sdkReference.current;
    const startSDK = async () => {
      try {
        await sdk.initialize();
        setStatus(SDKStatus.Ready);
      } catch (err) {
        logger.error("SDK Init Error:", err);
        setStatus(SDKStatus.Error);
        setError(err);
      }
    };

    startSDK();

    return () => {
      // Temizleme işlemini asenkron olarak başlatıyoruz
      sdk.cleanup().catch(e => logger.error("Cleanup error:", e));
    };
  }, []);

  const value = useMemo(() => ({
    loading: status === SDKStatus.Initializing,
    loaded: status === SDKStatus.Ready,
    sdk: sdkReference.current,
    error: error,
  }), [status, error]);

  return <SDKContext.Provider value={value}>{children}</SDKContext.Provider>;
};

export const useSDK = () => {
  const context = useContext(SDKContext);
  if (!context) {
    throw new Error("useSDK must be used within an SDKProvider");
  }
  return context;
};